import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  AppBarNavigation,
  BookTimeDialog,
  Button,
  buttonStylesLink,
  buttonStylesMenu,
  ConfirmDialog,
  getPersonaProps,
  iconButtonStylesAppBar,
  IconName,
  Notification,
  useAppNavigationContext,
  useClassNames,
  useTheme,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useHistory } from 'react-router-dom';

import { AppInstance_Status, AppInstance_Visibility } from '../../ai.h2o.cloud.appstore';
import { useEngineStateMonitorNotifications } from '../../aiem/engine/useEngineStateMonitorNotifications';
import { Workspace } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { AppStoreMenuName } from '../../discovery-service/constants';
import { useHandleNotifications } from '../../featurestore/api';
import { RoutePaths } from '../../pages/RoutePaths';
import { LocalStorageKeys, useEnv, useHandleSignOut, useInstance, useLocalStorage, useUser } from '../../utils/hooks';
import { HaicAboutModal } from '../HaicAboutModal/HaicAboutModal';
import { ProductLogo } from '../ProductLogo/ProductLogo';
import { WorkspacesDropdown } from './components/WorkspacesDropdown';
import { manageWorkspacesWrapperStyles, navLoadingStyles, signInButtonStyles } from './Navigation.styles';
import { useNavigationConfig } from './useNavigationConfig';

const NotificationsBellIcon = () => {
  useHandleNotifications();
  useEngineStateMonitorNotifications();
  return <Notification iconButtonProps={{ styles: iconButtonStylesAppBar }} />;
};

export const Navigation = () => {
  const [, setActiveWorkspace] = useLocalStorage<Workspace | undefined>(LocalStorageKeys.ACTIVE_WORKSPACE);
  const user = useUser();
  const { disableWorkspacesDropdown, workspacesDisabled } = useAppNavigationContext();
  const auth = useAuth();
  const env = useEnv();
  const history = useHistory();
  const { getInstancesList } = useInstance();
  const theme = useTheme();
  const logout = useHandleSignOut();
  const publicRoutesOnly = env?.publicModeEnabled && user.isPublic;

  const [instancePromptHidden, setInstancePromptHidden] = useState(true);
  const [instanceCount, setInstanceCount] = useState<number | null>(null);
  const [bookTimeHidden, setBookTimeHidden] = useState(true);
  const [hiddenAbout, { setFalse: showAbout, setTrue: hideAbout }] = useBoolean(true);

  const goToMyInstances = useCallback(() => {
    setInstancePromptHidden(true);
    history.push('/instances');
  }, [history, setInstancePromptHidden]);
  const onLogoutClick = useCallback(async () => {
    let count = 0;

    try {
      const instances = await getInstancesList({
        appId: '',
        includeAppDetails: false,
        allUsers: false,
        visibility: AppInstance_Visibility.VISIBILITY_UNSPECIFIED,
        allWorkspaces: false,
      });

      count = instances.filter((instance) =>
        [
          AppInstance_Status.DEPLOYED,
          AppInstance_Status.PENDING,
          AppInstance_Status.STATUS_UNSPECIFIED,
          AppInstance_Status.STATUS_UNKNOWN,
        ].includes(instance.status)
      ).length;
    } catch (err) {
      console.error(err);
    }

    setInstanceCount(count);

    if (count !== 0) {
      setInstancePromptHidden(false);
    } else {
      setActiveWorkspace(undefined);
      await logout();
    }
  }, [instanceCount, logout, getInstancesList]);
  const headerClick = () => history.push('/');

  const { isLoading: navigationLoading, ...navigationConfig } = useNavigationConfig({ onLogoutClick, showAbout });

  useEffect(() => {
    disableWorkspacesDropdown();
  }, []);

  const classNames = useClassNames('Navigation', navLoadingStyles) as any;

  const headerContent = (
    <Stack horizontal tokens={{ childrenGap: 8 }}>
      {!publicRoutesOnly && (
        <WorkspacesDropdown
          disabled={workspacesDisabled}
          onRenderLowerContent={() => (
            <div className={manageWorkspacesWrapperStyles(theme)}>
              <Link to={RoutePaths.MANAGE_WORKSPACES}>Manage Workspaces</Link>
            </div>
          )}
        />
      )}
      {env?.beamerEnabled && (
        <Button
          iconProps={{ iconName: 'Streaming', style: { fontSize: 22 } }}
          text={AppStoreMenuName.news}
          styles={buttonStylesLink}
          id="beamerButton"
        />
      )}
    </Stack>
  );

  return (
    <Stack className={classNames.root}>
      <AppBarNavigation
        logoElement={<ProductLogo />}
        personaProps={auth.user?.id_token ? getPersonaProps(env?.menu || {}) : undefined}
        onLogoClick={headerClick}
        onTitleClick={headerClick}
        userMenuItems={auth.user?.id_token ? navigationConfig.userMenuItems : undefined}
        navigationConfig={navigationConfig.navigationSectionConfig}
        hideNavigation={publicRoutesOnly || navigationLoading}
        headerContent={headerContent}
        menuButtonProps={{ styles: { ...buttonStylesMenu(theme), menuIcon: { color: theme.palette?.gray500 } } }}
      >
        {!auth.user?.id_token && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              text="Sign in"
              onClick={() =>
                auth.signinRedirect({
                  redirect_uri: window.location.origin + window.location.pathname + window.location.hash,
                })
              }
              styles={signInButtonStyles(theme)}
              iconName={IconName.ChevronRight}
            />
          </div>
        )}
        {!user.isPublic && <NotificationsBellIcon />}
      </AppBarNavigation>

      <ConfirmDialog
        hidden={instancePromptHidden}
        title="Terminate instances before logout?"
        content={
          <>
            You have <b>{instanceCount ?? 'Unknown'}</b> running app instance{instanceCount === 1 ? '' : 's'} that are
            consuming cluster resources, do you wish to review and terminate them before logging out?
          </>
        }
        confirmationButtonText="No, logout"
        dismissalButtonText="Yes, go to My Instances"
        onDismiss={goToMyInstances}
        onConfirm={logout}
      />
      <HaicAboutModal hidden={hiddenAbout} onDismiss={hideAbout} />
      <BookTimeDialog url={env?.menu?.bookTimeLink} onDismiss={() => setBookTimeHidden(true)} hidden={bookTimeHidden} />
    </Stack>
  );
};
